import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

export type UpcomingDeadlineKind = 'TASK' | 'RISK' | 'PAYMENT';

export type UpcomingDeadlineItem = {
    kind: UpcomingDeadlineKind;
    id: string;
    title: string;
    dueDate: string; // YYYY-MM-DD
    badge: string;
    dday: number | null;
};

type FilterKey = 'ALL' | 'PAYMENT' | 'RISK' | 'TASK';

type Props = {
    /** ALL 탭에서 보여줄 Top6 (merge+sort+slice 결과) */
    items: UpcomingDeadlineItem[];
    /** 각 탭별(최대 6개) 버킷 */
    buckets?: {
        task: UpcomingDeadlineItem[];
        risk: UpcomingDeadlineItem[];
        payment: UpcomingDeadlineItem[];
    };
};

function kindLabel(k: UpcomingDeadlineKind) {
    if (k === 'TASK') return 'Task';
    if (k === 'RISK') return 'Risk';
    return 'Payment';
}

function ddayText(dday: number | null) {
    if (dday == null) return '—';
    if (dday >= 0) return `D-${dday}`;
    return `D+${Math.abs(dday)}`;
}

function chipClass(active: boolean) {
    return [
        'text-[11px] rounded-full border px-2 py-1 transition',
        active ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
    ].join(' ');
}

export default function UpcomingDeadlines({ items, buckets }: Props) {
    const { projectId } = useParams<{ projectId: string }>();
    const [filter, setFilter] = useState<FilterKey>('ALL');

    const display = useMemo<UpcomingDeadlineItem[]>(() => {
        if (filter === 'ALL') return items ?? [];
        if (buckets) {
            if (filter === 'TASK') return buckets.task ?? [];
            if (filter === 'RISK') return buckets.risk ?? [];
            return buckets.payment ?? [];
        }
        if (filter === 'TASK') return (items ?? []).filter((x) => x.kind === 'TASK');
        if (filter === 'RISK') return (items ?? []).filter((x) => x.kind === 'RISK');
        return (items ?? []).filter((x) => x.kind === 'PAYMENT');
    }, [filter, items, buckets]);

    if (!projectId) {
        return <div className="text-sm text-rose-700">잘못된 프로젝트 ID입니다.</div>;
    }

    if (!items?.length && !buckets) {
        return <div className="text-sm text-gray-500">예정된 마감이 없습니다.</div>;
    }

    const toHref = (x: UpcomingDeadlineItem) => {
        if (x.kind === 'TASK') return `/projects/${projectId}/tasks?focus=${encodeURIComponent(x.id)}`;
        if (x.kind === 'RISK') return `/projects/${projectId}/risks?focus=${encodeURIComponent(x.id)}`;
        return `/projects/${projectId}/payments`;
    };

    return (
        <div>
            {/* Filters */}
            <div className="w-full mb-3 flex flex-wrap items-center gap-2">
                <button type="button" className={chipClass(filter === 'ALL')} onClick={() => setFilter('ALL')}>
                    All
                </button>
                <button
                    type="button"
                    className={chipClass(filter === 'PAYMENT')}
                    onClick={() => setFilter('PAYMENT')}
                >
                    Payment
                </button>
                <button type="button" className={chipClass(filter === 'RISK')} onClick={() => setFilter('RISK')}>
                    Risk
                </button>
                <button type="button" className={chipClass(filter === 'TASK')} onClick={() => setFilter('TASK')}>
                    Task
                </button>
            </div>

            {/* List */}
            {display.length === 0 ? (
                <div className="text-sm text-gray-500">선택한 항목이 없습니다.</div>
            ) : (
                // ✅ 여기! 기존 space-y-2(1열) → grid 2열
                <div className="grid w-full grid-cols-1 gap-2 md:grid-cols-2">
                    {display.map((x) => (
                        <Link
                            key={`${x.kind}:${x.id}`}
                            to={toHref(x)}
                            className="block w-full rounded-xl border bg-white p-3 transition hover:bg-gray-50"
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px] rounded-full border px-2 py-0.5 text-gray-600 bg-gray-50">
                                            {kindLabel(x.kind)}
                                        </span>
                                        <span className="text-[11px] rounded-full border px-2 py-0.5 text-gray-700 bg-white">
                                            {x.badge}
                                        </span>
                                    </div>

                                    <div className="mt-1 truncate text-sm font-medium text-gray-900">{x.title}</div>
                                    <div className="mt-0.5 text-xs text-gray-500">{x.dueDate}</div>
                                </div>

                                <div className="shrink-0 text-right">
                                    <div className="text-xs text-gray-500">D-day</div>
                                    <div className="text-sm font-semibold text-gray-900">{ddayText(x.dday)}</div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
